import { NetworkStatus, useSuspenseQuery } from '@apollo/client';
import type { FC } from 'react';
import { Suspense, useState } from 'react';

import {
  ClickReasonsEnum,
  EntityPageFocusType,
} from '@xing-com/crate-common-graphql-types';
import { ErrorBoundary } from '@xing-com/crate-core-error-boundary';
import {
  WarningMessageBox as Error,
  LazyLoading,
} from '@xing-com/crate-entity-pages-common';
import { usePageContext } from '@xing-com/crate-entity-pages-entity-pages-shared-context-entity-page-context';
import { Row } from '@xing-com/xing-grid';

import { SocialProofListDocument } from '../../../graphql/queries/social-proof-list-query.gql-types';

import { SocialProofItem } from './social-proof-item';
import { SocialProofListSkeleton } from './social-proof-list.skeleton';
import * as Styled from './social-proof-list.styles';

const QUERY_LIMIT = 20;

type Props = {
  listTitle: string;
};

const ErrorFallback: FC<{ onReloadClick?: () => void }> = ({
  onReloadClick = () => window.location.reload(),
}) => {
  return (
    <div data-testid="errorContainer">
      <Error
        headerText="EP_ERROR_HEADER"
        bodyText="EP_ERROR_BODY"
        buttonText="EP_ERROR_RELOAD_CTA"
        onClick={() => onReloadClick()}
      />
    </div>
  );
};

const SocialProofListInner: FC<Props> = ({ listTitle }) => {
  const { pageContext } = usePageContext();

  const [fetchMoreError, setFetchMoreError] = useState(false);

  const { data, error, refetch, fetchMore, networkStatus } = useSuspenseQuery(
    SocialProofListDocument,
    {
      variables: {
        id: pageContext.pageId,
        limit: QUERY_LIMIT,
        socialProofClickReasonsKey:
          pageContext.focusType === EntityPageFocusType.GroupPage
            ? ClickReasonsEnum.CrWebGroupsSocialProofHeaderMembers
            : ClickReasonsEnum.CrWebPublisherSocialProofHeader,
      },
      fetchPolicy: 'no-cache',
      notifyOnNetworkStatusChange: true,
    }
  );

  const getMoreItems = (cursor: string) => {
    fetchMore({
      variables: { limit: QUERY_LIMIT, after: cursor },
      updateQuery: (prev, { fetchMoreResult }) => {
        if (!fetchMoreResult) return prev;

        setFetchMoreError(false);

        const fetchMoreEntityPage =
          fetchMoreResult.entityPageEX?.__typename === 'EntityPage'
            ? fetchMoreResult.entityPageEX
            : undefined;
        const prevEntityPage =
          prev.entityPageEX?.__typename === 'EntityPage'
            ? prev.entityPageEX
            : undefined;

        if (!fetchMoreEntityPage || !prevEntityPage) {
          return prev;
        }

        const newEdges =
          fetchMoreEntityPage.userPageContext?.socialProof?.edges ?? [];

        if (prevEntityPage.userPageContext?.socialProof) {
          if (fetchMoreEntityPage?.userPageContext?.socialProof?.pageInfo) {
            prevEntityPage.userPageContext.socialProof.pageInfo =
              fetchMoreEntityPage.userPageContext.socialProof.pageInfo;
          }

          if (prevEntityPage.userPageContext.socialProof.edges) {
            prevEntityPage.userPageContext.socialProof.edges = [
              ...prevEntityPage.userPageContext.socialProof.edges,
              ...newEdges,
            ];
          }
        }

        return {
          entityPageEX: {
            ...prevEntityPage,
          },
        };
      },
    }).catch((err) => {
      if (err) {
        setFetchMoreError(true);
      }
    });
  };

  const socialProof =
    data?.entityPageEX?.__typename === 'EntityPage'
      ? data?.entityPageEX?.userPageContext?.socialProof
      : undefined;

  const socialProofEdges = socialProof?.edges;
  const socialProofNodes = socialProofEdges
    ?.map((edge) => edge?.node)
    .filter((e) => !!e);

  const cursor = socialProof?.pageInfo?.hasNextPage
    ? socialProof?.pageInfo?.endCursor
    : undefined;
  const hasNextPage = socialProof?.pageInfo.hasNextPage;

  return (
    <>
      <Row>
        <Styled.Container size={12} sizeDesktop={5} sizeTablet={5}>
          {socialProofNodes ? (
            <>
              {listTitle ? (
                <Styled.Title lineHeight="dense" fontWeight="bold" size="large">
                  {listTitle}
                </Styled.Title>
              ) : null}
              {socialProofNodes.length !== 0 ? (
                <Styled.List data-testid="SOCIAL_PROOF_LIST">
                  <>
                    {socialProofNodes.map((user, index) => (
                      <SocialProofItem key={index} user={user} />
                    ))}
                  </>
                </Styled.List>
              ) : null}
            </>
          ) : null}
          {error || fetchMoreError ? (
            <ErrorFallback
              onReloadClick={() => {
                if (fetchMoreError) {
                  if (cursor) getMoreItems(cursor);
                } else {
                  refetch();
                }
              }}
            />
          ) : null}
        </Styled.Container>
      </Row>
      <LazyLoading
        type="LAZY_SCROLL"
        isLoading={networkStatus === NetworkStatus.fetchMore}
        enabled={!!hasNextPage}
        onLazyLoad={() => {
          if (cursor) getMoreItems(cursor);
        }}
      />
    </>
  );
};

const LoadingSkeleton: FC = () => {
  return (
    <Row>
      <Styled.Container size={12} sizeDesktop={5} sizeTablet={5}>
        <Styled.List>
          <SocialProofListSkeleton numberOfItems={2} />
        </Styled.List>
      </Styled.Container>
    </Row>
  );
};

export const SocialProofList: FC<Props> = (props) => {
  return (
    <ErrorBoundary
      onError={() => (
        <Row>
          <Styled.Container size={12} sizeDesktop={5} sizeTablet={5}>
            <ErrorFallback />
          </Styled.Container>
        </Row>
      )}
    >
      <Suspense fallback={<LoadingSkeleton />}>
        <SocialProofListInner {...props} />
      </Suspense>
    </ErrorBoundary>
  );
};
